import _ from 'underscore';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { Status } from '@bingads-webui-campaign-react/diagnostic-card';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';

import { getInvalidEventStrings } from './get-invalid-event-strings';
import { statusAndActionReport, auditPointDisplayed, getFullProductUrl } from './utils';
import { DetailCardsKey } from '../const';

const STATUS_TEXT = {
  NotSetUpGoal: _TL_('No conversion goal'),
  NotSetUpGoalMaxClicks: _TL_('No conversion goal'),
  Unverified: _TL_('Unverified UET tag'),
  UnverifiedMaxClicks: _TL_('Unverified UET tag'),
  Active: _TL_('Recording conversions'),
  RecordingConversions: _TL_('Recording conversions'),
  NoConversions: _TL_('No conversions'),
  NoConversionsMaxClicks: _TL_('No conversions'),
  NoConversionGoal: _TL_('No conversion goal'),
  NoConversionGoalMaxClicks: _TL_('No conversion goal'),
  Pending: _TL_('Pending'),
  PendingMaxClicks: _TL_('Pending'),
  MissGoalWithRevenueValue: _TL_('No goal to track revenue'),
  MissGoalWithRevenueValueMaxClicks: _TL_('No goal to track revenue'),
  InactiveDueToTagUnavailable: _TL_('Unavailable UET'),
  InactiveDueToTagUnavailableMaxClicks: _TL_('Unavailable UET'),
  TagInactive: _TL_('Inactive UET tag'),
  TagInactiveMaxClicks: _TL_('Inactive UET tag'),
  NoRecentConversion: _TL_('No recent conversions'),
  NoRecentConversionMaxClicks: _TL_('No recent conversions'),
};

const AUDIT_POINT_STATUS_TEXT = {
  TrackingStatusUnverified: _TL_('Unverified UET tag'),
  TrackingStatusTagInactive: _TL_('Inactive UET tag'),
  TrackingStatusInactiveDueToTagUnavailable: _TL_('Incorrect UET tag'),
  TrackingStatusUnverified_UETMismatch: _TL_('UET Unverified'),
  TrackingStatusTagInactive_UETMismatch: _TL_('UET Inactive'),
  TrackingStatusInactiveDueToTagUnavailable_UETMismatch: _TL_('UET Unavailable'),
};

const AUDIT_POINT_TITLE_TEXT = {
  TrackingStatusUnverified: _TL_('UET tag is not verified'),
  TrackingStatusTagInactive: _TL_('UET tag is inactive'),
  TrackingStatusInactiveDueToTagUnavailable: _TL_('UET tag is incorrect'),
};

const OPERATORS_TEXT = {
  EqualsTo: _TL_('Equals to'),
  BeginsWith: _TL_('Begins with'),
  Contains: _TL_('Contains'),
  RegularExpression: _TL_('Regular expression'),
};

const CHANGE_TO_MAIN_GOAL_TEXT = {
  TrackingStatusNoConversions_ChangeToMainGoal: _TL_('Your campaign has not recorded any conversions in the past seven days. We recommend setting {{GoalName}} as the campaign\'s conversion goal or changing this goal to “include in conversions”, as it may help optimize this campaign for better performance.'),
  TrackingStatusUnverified_ChangeToMainGoal: _TL_('We haven\'t received any user activity data from the UET tag on your website. We recommend setting {{GoalName}} as the campaign\'s conversion goal or changing this goal to “include in conversions”, as it may help optimize this campaign for better performance.'),
  TrackingStatusTagInactive_ChangeToMainGoal: _TL_('We haven\'t received any user activity data from the UET tag in the last 24 hours. We recommend setting {{GoalName}} as the campaign\'s conversion goal or changing this goal to “include in conversions”, as it may help optimize this campaign for better performance.'),
  TrackingStatusInactiveDueToTagUnavailable_ChangeToMainGoal: _TL_('The account no longer has access to the UET tag. We recommend setting {{GoalName}} as the campaign\'s conversion goal or changing this goal to “include in conversions”, as it may help optimize this campaign for better performance.'),
  ConversionGoalPending_ChangeToMainGoal: _TL_('We are waiting for this campaign to record conversions. We recommend setting {{GoalName}} as the campaign\'s conversion goal or changing this goal to “include in conversions”, as it may help optimize this campaign for better performance.'),
};

const getCardDetails = ({
  auditPoint, scope, i18n, activity, locale, serverContext, campaignType, details,
}) => {
  const onClickNavigateToUETTagPage = () => {
    statusAndActionReport({
      activity,
      type: 'conversion',
      action: 'navigate to uet tag page',
      details: `AuditPoint: ${auditPoint}`,
    });

    if (!isSDK()) {
      scope.remove(['campaignId']);
      scope.save(getRoutePathById(allSiteMap, 'uet-tag'));
    } else {
      const url = getFullProductUrl('uet-tag', scope);
      window.open(url);
    }
  };

  const onClickNavigateToOfflineConversionsPage = () => {
    statusAndActionReport({
      activity,
      type: 'conversion',
      action: 'navigate to offline conversions page',
      details: `AuditPoint: ${auditPoint}`,
    });

    if (!isSDK()) {
      scope.remove(['campaignId']);
      scope.save(getRoutePathById(allSiteMap, 'offline-conversions'));
    } else {
      const url = getFullProductUrl('offline-conversions', scope);
      window.open(url);
    }
  };

  const onClickNavigateToConversionGoalsPage = () => {
    statusAndActionReport({
      activity,
      type: 'conversion',
      action: 'navigate to conversion goals page',
      details: `AuditPoint: ${auditPoint}`,
    });

    if (!isSDK()) {
      scope.remove(['campaignId']);
      scope.save(getRoutePathById(allSiteMap, 'conversion-goals'));
    } else {
      const url = getFullProductUrl('conversion-goals', scope);
      window.open(url);
    }
  };

  auditPointDisplayed({ activity, auditPoint, area: 'Conversion' });
  let strings = {};

  if (campaignType !== CampaignTypes.Audience && (_.get(serverContext, ['DynamicPermissions', 'DiagnosticCMMTApi']) || _.get(serverContext, ['DynamicPermissions', 'ConversionWarningImprovement']))) {
    switch (auditPoint) {
      case 'HasNoValidGoalAtCampaignOrAccountLevel':
      case 'HasNoValidGoalAtCampaignOrAccountLevel_NoAccountGoal':
        return {
          cardTitle: i18n.getString(_TL_('Configure conversion goal')),
          cardMsg: i18n.getString(_TL_('This campaign doesn\'t have a conversion goal configured. Please review your campaign settings to assign a goal or create a new conversion goal.')),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56709/2`,
        };

      case 'HasNoValidGoalAtCampaignOrAccountLevel_ChangeToMainGoal':
        return {
          cardTitle: i18n.getString(_TL_('Configure conversion goals')),
          cardMsg: i18n.getString(_TL_('Your campaign is tracking conversions, but “Include in conversions” is not selected. Please add a campaign level goal or update your existing goals to include conversions.')),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/60138/2-500`,
        };

      case 'TrackingStatusNoConversions_NotUploadOfflineConversion':
        if (!details || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        return {
          cardTitle: i18n.getString(_TL_('Upload offline conversions')),
          cardMsg: i18n.getString(_TL_('There are no offline conversions uploaded for {{GoalName}}. Please visit your offline goals to upload conversions associated to this campaign.'), details.GoalWithTopPriority),
          onCardButtonClicked: onClickNavigateToOfflineConversionsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56852/1/#ext:conversiongoals`,
        };

      case 'TrackingStatusNoConversions_EventGoalInvalidParameter':
        if (!details || !details.FixEventGoal || (!details.FixEventGoal || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName)) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        // eslint-disable-next-line no-case-declarations
        const { current, suggested } = getInvalidEventStrings({ i18n, fix: details.FixEventGoal });
        if (_.isEmpty(current) || _.isEmpty(suggested)) {
          activity.error(`Details string missing for audit point ${auditPoint} - details: ${JSON.stringify(details)} current: ${current} suggested: ${suggested}`);
        }
        return {
          cardTitle: i18n.getString(_TL_('Conversion goal setting error')),
          cardMsg: i18n.getString(_TL_('We have identified a potential error in your conversion goal event parameters for {{GoalName}}. The current setting is {{current}}. We recommend changing this to {{suggested}}.'), { current, suggested, GoalName: details.GoalWithTopPriority.GoalName }),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56709/2-500`,
        };

      case 'TrackingStatusNoConversions_UrlGoalInvalidParameter':
        if (!details || !details.FixDestinationUrlGoal
          || !details.FixDestinationUrlGoal.CurrentDestinationUrlOperatorName || !details.FixDestinationUrlGoal.CurrentDestinationUrl
          || !details.FixDestinationUrlGoal.SuggestedDestinationUrlOperatorName || !details.FixDestinationUrlGoal.SuggestedDestinationUrl
          || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName
        ) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        // eslint-disable-next-line no-case-declarations
        strings = {
          ...details.FixDestinationUrlGoal,
          CurrentDestinationUrlOperatorName: i18n.getString(OPERATORS_TEXT[details.FixDestinationUrlGoal.CurrentDestinationUrlOperatorName]),
          SuggestedDestinationUrlOperatorName: i18n.getString(OPERATORS_TEXT[details.FixDestinationUrlGoal.SuggestedDestinationUrlOperatorName]),
          GoalName: details.GoalWithTopPriority.GoalName,
        };
        return {
          cardTitle: i18n.getString(_TL_('Invalid Destination URL')),
          cardMsg: i18n.getString(_TL_('Your campaign has not recorded any conversions in the past seven days. The current URL setting for {{GoalName}} is "{{CurrentDestinationUrlOperatorName}} {{CurrentDestinationUrl}}". We recommend changing this to "{{SuggestedDestinationUrlOperatorName}} {{SuggestedDestinationUrl}}".'), strings),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56709/2-500`,
        };

      case 'TrackingStatusNoConversions_UETMismatch':
        if (!details || !details.UETMismatch || !details.UETMismatch.SuggestedTagName
          || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName
        ) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        // eslint-disable-next-line no-case-declarations
        strings = {
          SuggestedTagName: details.UETMismatch.SuggestedTagName,
          GoalName: details.GoalWithTopPriority.GoalName,
        };
        return {
          statusLabelMsg: i18n.getString(_TL_('Incorrectly matched UET tags')),
          cardTitle: i18n.getString(_TL_('Incorrectly matched UET tags')),
          cardMsg: i18n.getString(_TL_('Your campaign has not recorded any conversions in the past seven days. Your campaign is recording conversion events from a different UET tag for {{GoalName}}. We recommend using {{SuggestedTagName}}. Please check that the goal is using the correct UET tag.'), strings),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56685/2-500`,
        };

      case 'TrackingStatusNoConversions_UETActiveMissingConversionEvent':
        if (!details || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        return {
          cardTitle: i18n.getString(_TL_('Missing conversion events')),
          cardMsg: i18n.getString(_TL_('Your campaign has not recorded any conversions in the past seven days. We\'ve detected the UET tag on your website, but there have been no conversion events matching your definition in the last seven days for {{GoalName}}. Check that the goal has the correct event definition.'), details.GoalWithTopPriority),
          onCardButtonClicked: onClickNavigateToUETTagPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56951/2`,
        };

      case 'TrackingStatusNoConversions':
        return {
          cardTitle: i18n.getString(_TL_('No recent conversions')),
          cardMsg: i18n.getString(_TL_('Your campaign has not recorded any conversions in the past seven days. Please review common issues related to conversion tracking, conversion goals and offline conversions. For further questions, please contact Support.')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/60398/2`,
        };

      case 'TrackingStatusUnverified_UETMismatch':
      case 'TrackingStatusTagInactive_UETMismatch':
      case 'TrackingStatusInactiveDueToTagUnavailable_UETMismatch':
        if (!details || !details.UETMismatch || !details.UETMismatch.SuggestedTagName
          || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName
        ) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        strings = {
          SuggestedTagName: details.UETMismatch.SuggestedTagName,
          GoalName: details.GoalWithTopPriority.GoalName,
        };
        return {
          statusLabelMsg: i18n.getString(_.get(AUDIT_POINT_STATUS_TEXT, auditPoint, _TL_(''))),
          cardTitle: i18n.getString(_TL_('Incorrectly matched UET tags')),
          cardMsg: i18n.getString(_TL_('Your campaign has not recorded any conversions in the past seven days. Your campaign is recording conversion events from a different UET tag for {{GoalName}}. We recommend using {{SuggestedTagName}} and ensure that the goal is using the correct UET tag.'), strings),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56951/2`,
        };

      case 'TrackingStatusUnverified':
        return {
          cardTitle: i18n.getString(_TL_('UET tag is not verified')),
          cardMsg: i18n.getString(_TL_('We haven\'t received any user activity data from the UET tag on your website. It can take up to 24 hours for Microsoft Advertising to verify.')),
          onCardButtonClicked: onClickNavigateToUETTagPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56951/2`,
        };

      case 'TrackingStatusTagInactive':
        return {
          cardTitle: i18n.getString(_TL_('Inactive UET tag')),
          cardMsg: i18n.getString(_TL_('We haven\'t received any user activity data from the UET tag in the last 24 hours. Make sure that the UET tag tracking code is still on your website.')),
          onCardButtonClicked: onClickNavigateToUETTagPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56951/2`,
        };

      case 'TrackingStatusInactiveDueToTagUnavailable':
        return {
          cardTitle: i18n.getString(_TL_('Unavailable UET')),
          cardMsg: i18n.getString(_TL_('The account no longer has access to the UET tag. It is possible that the UET tag permissions in another manager account were removed, or this account was transferred and subsequently lost permission to a UET tag in a previous manager account.')),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56951/2`,
        };

      case 'MissGoalWithRevenueValue':
        return {
          cardTitle: i18n.getString(_TL_('Revenue value not set up')),
          cardMsg: i18n.getString(_TL_('You haven\'t set a specific revenue value for this conversion goal.')),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56687/2`,
        };

      case 'TrackingStatusNoConversions_ChangeToMainGoal':
      case 'TrackingStatusUnverified_ChangeToMainGoal':
      case 'TrackingStatusTagInactive_ChangeToMainGoal':
      case 'TrackingStatusInactiveDueToTagUnavailable_ChangeToMainGoal':
      case 'ConversionGoalPending_ChangeToMainGoal':
        if (!details || !details.GoalWithTopPriority || !details.GoalWithTopPriority.GoalName) {
          activity.error(`Details missing for audit point ${auditPoint} - details: ${JSON.stringify(details)}`);
        }
        return {
          cardTitle: i18n.getString(_TL_('Change goal to “include in conversions”')),
          cardMsg: i18n.getString(_.get(CHANGE_TO_MAIN_GOAL_TEXT, auditPoint, _TL_('')), details.GoalWithTopPriority),
          onCardButtonClicked: onClickNavigateToConversionGoalsPage,
          cardButtonText: i18n.getString(_TL_('Set up now')),
          learnMoreLink: `https://help.ads.microsoft.com/?#apex/ads/${locale}/56920/1`,
        };

      case 'ConversionGoalPending':
        return {
          cardTitle: i18n.getString(_TL_('Check your conversion tracking')),
          cardMsg: i18n.getString(_TL_('Tag verified at least for one conversion goal, but no conversion data yet.')),
        };

      case 'TrackingStatusRecordingConversions':
        return {};

      default:
        activity.error(`Audit point mapping not found for new API in getConversionStatus:${auditPoint}`);
        return {};
    }
  }

  switch (auditPoint) {
    case 'HasNoValidGoalAtCampaignOrAccountLevel':
    case 'HasNoValidGoalAtCampaignOrAccountLevel_NoAccountGoal':
      return {
        cardTitle: i18n.getString(_TL_('Create conversion goal')),
        cardMsg: i18n.getString(_TL_('Your campaign doesn\'t have a conversion goal to track conversions. When you create a conversion goal and associate it with this campaign, you can specify which actions count  as conversions.')),
        onCardButtonClicked: () => {
          statusAndActionReport({
            activity,
            type: 'conversion',
            action: 'navigate to goal creation page',
            details: `AuditPoint: ${auditPoint}`,
          });

          const isAutoGoalEnabled = _.get(serverContext.DynamicPermissions, 'AllowAutoGoalWizard', false);
          let routeName = 'conversion-goals-create';

          if (isAutoGoalEnabled) {
            routeName = 'conversion-goals-auto-create';
          }

          if (!isSDK()) {
            scope.remove(['campaignId']);
            scope.save(getRoutePathById(allSiteMap, routeName));
          } else {
            const url = getFullProductUrl(routeName, scope);
            window.open(url);
          }
        },
        cardButtonText: i18n.getString(_TL_('Set up now')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56709/2`,
      };

    case 'MissGoalWithRevenueValue':
      return {
        statusLabelMsg: i18n.getString(_TL_('No goal to track revenue')),
        cardTitle: i18n.getString(_TL_('Check you conversion tracking')),
        cardMsg: i18n.getString(_TL_('Something doesn\'t seem right with your conversion tracking. Please check your conversion tracking setup.')),
        onCardButtonClicked: () => {
          statusAndActionReport({
            activity,
            type: 'conversion',
            action: 'navigate to conversion goals page',
            details: `AuditPoint: ${auditPoint}`,
          });

          if (!isSDK()) {
            scope.remove(['campaignId']);
            scope.save(getRoutePathById(allSiteMap, 'conversion-goals'));
          } else {
            const url = getFullProductUrl('conversion-goals', scope);
            window.open(url);
          }
        },
        cardButtonText: i18n.getString(_TL_('Check now')),
      };

    case 'TrackingStatusUnverified':
    case 'TrackingStatusTagInactive':
    case 'TrackingStatusInactiveDueToTagUnavailable':
      return {
        statusLabelMsg: i18n.getString(_.get(AUDIT_POINT_STATUS_TEXT, auditPoint, _TL_(''))),
        cardTitle: i18n.getString(_.get(AUDIT_POINT_TITLE_TEXT, auditPoint, _TL_(''))),
        cardMsg: i18n.getString(_TL_('Something doesn\'t seem right with your UET tag. Please check your UET tag setup.')),
        onCardButtonClicked: onClickNavigateToUETTagPage,
        cardButtonText: i18n.getString(_TL_('Check now')),
        learnMoreLink: `https://help.ads.microsoft.com/apex/index/3/${locale}/56692`,
      };

    case 'TrackingStatusNoConversions':
      return {
        cardTitle: i18n.getString(_TL_('There are no conversions for this campaign')),
        cardMsg: i18n.getString(_TL_('You\'ve received no conversions for this campaign in the past 7 days. Please review common issues with UET tracking, conversion goals, and offline conversions or contact Support for additional help.')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/60398/2`,
      };

    case 'NoOfflineConversions':
      return {
        cardTitle: i18n.getString(_TL_('There are no conversions for this campaign')),
        cardMsg: i18n.getString(_TL_('There are no conversions recorded for this campaign in the past 7 days. Please check the UET setup for your online conversion tracking. Please also ensure your import tool is operating correctly, and that your offline conversions are imported.')),
        onCardButtonClicked: onClickNavigateToOfflineConversionsPage,
        cardButtonText: i18n.getString(_TL_('Fix now')),
        learnMoreLink: `https://help.ads.microsoft.com/#apex/ads/${locale}/56852/2`,
      };

    case 'ConversionGoalPending':
      return {
        cardTitle: i18n.getString(_TL_('Check your conversion tracking')),
        cardMsg: i18n.getString(_TL_('Tag verified at least for one conversion goal, but no conversion data yet.')),
      };

    case 'TrackingStatusRecordingConversions':
    case 'HasOfflineConversions':
    case 'HasValidAccountLevelGoal':
      return {};

    default:
      activity.error(`Audit point mapping not found in getConversionStatus:${auditPoint}`);
      return {};
  }
};

export const isConversionApplicable = ({
  conversionDetails,
}) => {
  const statusValue = _.get(conversionDetails, ['Value', 'Status']);
  return statusValue !== 'ConversionNotApplicable';
};

export const getConversionStatus = ({
  details, scope, i18n, activity, locale, serverContext, campaignType,
}) => {
  if (_.isEmpty(details)
    || _.isEmpty(_.get(details, 'Value'))
    || _.get(details, ['Value', 'ErrorCode'], 0) !== 0
    || _.get(details, ['Value', 'ErrorMessage'])) {
    statusAndActionReport({
      activity,
      type: 'conversion',
      details: `Error - details: ${JSON.stringify(details)}`,
    });
    return {
      name: i18n.getString(_TL_('Conversions')),
      key: DetailCardsKey.conversionTracking,
    };
  }

  const statusValue = _.get(details, ['Value', 'Status']);
  const auditPoint = _.get(details, ['Value', 'AuditPoints', 0]);
  const parsed = JSON.parse(_.get(details, ['Value', 'DiagnosticDetails'], '{}'));
  const diagnosticDetails = { ..._.get(parsed, ['GoalWithTopPriorityDiagnose'], {}), GoalWithTopPriority: _.get(parsed, ['GoalWithTopPriority'], {}) };

  const cardDetails = getCardDetails({
    auditPoint, scope, i18n, activity, locale, serverContext, campaignType, details: diagnosticDetails,
  });

  let status = Status.Error;
  if (statusValue === 'Active' || statusValue === 'RecordingConversions') {
    status = Status.Active;
  } else if (statusValue === 'Pending' || statusValue === 'PendingMaxClicks') {
    status = Status.Pending;
  } else if (statusValue === 'NoConversions' || (statusValue && statusValue.includes('MaxClicks'))) {
    status = Status.Warning;
  }

  return {
    name: i18n.getString(_TL_('Conversions')),
    key: DetailCardsKey.conversionTracking,

    status,
    statusLabelMsg: i18n.getString(_.get(STATUS_TEXT, statusValue, _TL_(''))),

    ...cardDetails,
  };
};
